import React, { useState, useEffect, useMemo } from "react";
import { Timeline } from "@material-tailwind/react";
import { ArrowRight, Download, X } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../services/api/axios";
import { useUser } from "../context/UserContext";
import { PrimaryButton, SecondaryButton } from "./Buttons";
import { fetchProjects } from "../services/projects";
import CustomSelect from "./CustomSelect";
import { FormModal } from "./form";

const BulkUploadAgents = ({ isOpen, onClose, onUploadSuccess, projectId }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [step, setStep] = useState(0);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [errors, setErrors] = useState([]);
  const [backendUploadMessage, setBackendUploadMessage] = useState("");
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || "");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const { userInfo } = useUser();

  const resetState = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setStep(0);
    setIsUploadSuccess(false);
    setErrors([]);
    setBackendUploadMessage("");
    setIsFileUploaded(false);
    setSelectedProjectId(projectId || "");
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file =
      e.target.files?.[0] || (e.dataTransfer ? e.dataTransfer.files[0] : null);

    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
        toast.error("Please select a valid CSV file");
        return;
      }
      setSelectedFile(file);
      setUploadProgress(0);
      setIsUploading(true);
      setIsFileUploaded(true);
    }
  };

  const uploadCSV = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    if (!selectedProjectId) {
      toast.error("Please select a project for bulk upload");
      setBackendUploadMessage("Please select a project for bulk upload");
      return;
    }

    setIsSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("csv_file", selectedFile);

      const developerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
      
      const response = await axiosInstance.post(
        `/customers/bulk/csv?projectId=${selectedProjectId}&developerId=${developerId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      if (response && Array.isArray(response)) {
        setIsUploadSuccess(true);
        setStep(3);
        setBackendUploadMessage(`Successfully uploaded ${response.length} agents`);
        toast.success(`Successfully uploaded ${response.length} agents!`);

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        // Close the modal after successful upload
        onClose();
      } else {
        setIsUploadSuccess(false);
        setBackendUploadMessage("Upload completed but response format is unexpected");
        toast.warn("Upload completed but response format is unexpected");
      }
    } catch (error) {
      let errorMessage = "";
      if (error.response && error.response.status === 422) {
        errorMessage =
          "Validation Error: " +
          (error.response.data?.message || "Please check your CSV data.");
        toast.error(errorMessage);
        setIsUploadSuccess(false);
      } else if (error.response && error.response.status === 400) {
        errorMessage =
          "Bad Request: " +
          (error.response.data?.message || "Please check your CSV format and data.");
        toast.error(errorMessage);
        setIsUploadSuccess(false);
      } else {
        errorMessage =
          error.response?.data?.message ||
          "An error occurred during the upload. Please try again.";
        toast.error(errorMessage);
      }
      setBackendUploadMessage(errorMessage);
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
      setIsSubmitLoading(false);
    }
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    setBackendUploadMessage("");
    setIsFileUploaded(false);
  };

  const handleSubmit = () => {
    if (isFileUploaded) {
      uploadCSV();
    } else {
      toast.error("Please select a file to upload");
    }
  };

  useEffect(() => {
    let interval;
    if (isUploading) {
      interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            return 100;
          }
          return prev + 20;
        });
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isUploading]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    } else {
      // Fetch projects when modal opens
      fetchProjectsList();
    }
  }, [isOpen]);

  // Update selectedProjectId when projectId prop changes
  useEffect(() => {
    setSelectedProjectId(projectId || "");
  }, [projectId]);

  // Fetch projects
  const fetchProjectsList = async () => {
    try {
      setLoadingProjects(true);
      const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
      const projectResponse = await fetchProjects({
        customerId: customerId,
      });
      if (projectResponse) {
        setProjects(projectResponse);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to fetch projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.projectId, label: p.projectName })),
    [projects],
  );
  const selectedProjectOption =
    projectOptions.find((o) => String(o.value) === String(selectedProjectId)) || null;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      title="Bulk Upload Channel Partners"
      size="lg"
      footer={
        step !== 3 ? (
          <div className="flex justify-between w-full gap-4">
            <SecondaryButton
              className="w-32"
              disabled={step === 0 || isSubmitLoading}
              onClick={() => setStep(step - 1)}
            >
              Previous
            </SecondaryButton>
            {step !== 1 ? (
              <PrimaryButton
                className="w-32 bg-flamingo-500 border-none"
                icon={<ArrowRight className="ml-2 size-4" />}
                disabled={isSubmitLoading}
                onClick={() => setStep(step + 1)}
              >
                Next
              </PrimaryButton>
            ) : (
              <PrimaryButton
                className="w-32 bg-flamingo-500 border-none"
                icon={isSubmitLoading ? null : <ArrowRight className="ml-2 size-4" />}
                disabled={isSubmitLoading || !selectedProjectId || !selectedFile}
                onClick={handleSubmit}
              >
                {isSubmitLoading ? "Loading..." : "Finish"}
              </PrimaryButton>
            )}
          </div>
        ) : null
      }
    >
          <div className="w-full text-center">
            <Timeline
              mode="stepper"
              value={step}
              onChange={(val) => setStep(Number(val))}
              className="w-full p-4 px-28 "
            >
              <Timeline.Item
                disabled={step < 0}
                value={0}
                className="w-full "
              >
                <Timeline.Header>
                  <Timeline.Separator />
                  <Timeline.Icon className={step === 0 ? "bg-flamingo-500 text-white" : " bg-flamingo-500 text-gray-700"}>
                    1
                  </Timeline.Icon>
                </Timeline.Header>
              </Timeline.Item>
              <Timeline.Item
                disabled={step < 1}
                value={1}
                className="w-full"
              >
                <Timeline.Header>
                  <Timeline.Separator />
                  <Timeline.Icon className={step === 1 ? "bg-flamingo-500 text-white" : "bg-gray-200 text-gray-700"}>
                    2
                  </Timeline.Icon>
                </Timeline.Header>
              </Timeline.Item>
              <Timeline.Item disabled={step < 1} value={2}>
                <Timeline.Header>
                  <Timeline.Separator />
                  <Timeline.Icon className={step === 2 ? "bg-flamingo-500 text-white" : "bg-gray-200 text-gray-700"}>
                    3
                  </Timeline.Icon>
                </Timeline.Header>
              </Timeline.Item>
            </Timeline>

            <div className="flex flex-col items-center pb-4">
              {step === 0 && (
                <div className="mt-4 w-full p-4">
                  <p className="font-medium text-md ">
                    Follow these steps to prepare your bulk upload:
                  </p>
                  <div className="w-full max-w-6xl bg-white shadow-md rounded-lg p-6 mt-2">
                    {/* Step 1 */}
                    <div className="pb-6">
                      <h3 className="font-semibold text-md text-accent">
                        Step 1: Download the CSV Template
                      </h3>
                      <p className="text-gray-700 text-sm mt-2 font-normal">
                        Download the CSV template provided below to start your bulk upload process.
                        This template contains all the required fields for creating channel partner accounts.
                      </p>

                      <a
                        href="/channel_partner_data.csv"
                        download
                        className="bg-flamingo-500 items-center text-white py-2 px-4 rounded hover:bg-flamingo-600 transition mt-4 inline-block"
                      >
                        <Download className="w-6 h-6" />
                      </a>
                    </div>

                    {/* Step 2 */}
                    <div className="pb-6">
                      <h3 className="font-semibold text-md text-accent">
                        Step 2: Select Project and Upload CSV
                      </h3>
                      <p className="text-gray-700 text-sm mt-2 font-normal">
                        Select the project where you want to create channel partner accounts, then upload your CSV file.
                        Make sure each field is correctly filled and the file is saved as .csv format.
                        <br /><br />
                        
                      </p>
                    </div>

                    {/* Step 3 */}
                    <div className="pb-6">
                      <h3 className="font-semibold text-md text-accent">
                        Step 3: Upload the CSV File
                      </h3>
                      <p className="text-gray-700 text-sm mt-2 font-normal">
                        Once your template is filled and saved as a CSV,
                        click next and upload the file . The channel partners will be created and assigned to the selected project.
                      </p>
                      {selectedProjectId && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-sm text-blue-800">
                            <strong>Selected Project:</strong> {projects.find(p => p.projectId === selectedProjectId)?.projectName}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="flex flex-col items-center mt-4">
                  <div className="w-full mb-6 text-left">
                    <CustomSelect
                      label="Select Project"
                      required
                      placeholder={loadingProjects ? "Loading projects..." : "Select a project"}
                      options={projectOptions}
                      value={selectedProjectOption}
                      onChange={(opt) => setSelectedProjectId(opt?.value ?? "")}
                      isDisabled={loadingProjects}
                      styles={{}}
                      classNamePrefix="select"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      size="form"
                    />
                  </div>

                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileChange}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        document.getElementById("file-upload").click();
                      }
                    }}
                    className="w-full p-16 px-20 border-2 border-dashed border-gray-300 rounded-lg text-medium text-center hover:bg-blue-100 hover:border-accent hover:text-blue-800 transition duration-300 ease-in-out"
                  >
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      hidden
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer text-gray-500"
                    >
                      <span>Drag & Drop or Click to Upload CSV</span>
                    </label>
                  </div>

                  {selectedFile && (
                    <div className="mt-6 p-4 w-full bg-white border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <img
                            src="/csv.png"
                            alt="CSV Icon"
                            className="w-8 h-8"
                          />
                          <span>{selectedFile.name}</span>
                        </div>
                        <div className="text-sm text-gray-600 ml-4">
                          {selectedFile.size && (
                            <span>
                              {Math.round(selectedFile.size / 1024)} KB
                            </span>
                          )}
                        </div>

                        <button
                          className="text-red-500 ml-4 hover:text-red-700 transition"
                          onClick={handleCancelUpload}
                        >
                          <X className="text-xl" />
                        </button>
                      </div>

                      <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-blue-500 h-2 transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Uploading: {uploadProgress}%
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isUploadSuccess && (
                <div></div>
              )}

              {errors && errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-red-500 font-medium">
                    Validation Errors:
                  </p>
                  <ul className="text-red-500 list-disc list-inside">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

          </div>
    </FormModal>
  );
};

export default BulkUploadAgents; 